import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Snackbar,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion, AnimatePresence } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';

const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};

const GradientTableHead = styled(TableHead)(({ theme }) => ({

    background: "#f5f7fa",
    '& .MuiTableCell-head': {
        color: "#24803F",
        fontWeight: 'bold',
        fontSize: '1rem',
    },
}));


const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut",
            when: "beforeChildren",
            staggerChildren: 0.1
        }
    }
};


const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.3,
            ease: "easeOut"
        }
    },
    exit: {
        opacity: 0,
        height: 0,
        transition: {
            duration: 0.3,
            ease: "easeIn"
        }
    }
};

const Contact = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [openDialog, setOpenDialog] = useState(false);
    const [contactToDeleteId, setContactToDeleteId] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);

    const handleOpenDetails = (contact) => {
        setSelectedContact(contact);
        setOpenDetailsModal(true);
    };

    const handleCloseDetails = () => {
        setSelectedContact(null);
        setOpenDetailsModal(false);
    };

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                const response = await fetch(`${BASE_URL}/contacts`);
                if (!response.ok) {

                    throw new Error('Failed to fetch contacts');
                }
                const data = await response.json();
                setContacts(data.contacts.reverse());
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchContacts();
    }, []);


    const handleOpenDialog = (id) => {
        setContactToDeleteId(id);
        setOpenDialog(true);
    };


    const handleCloseDialog = () => {
        setOpenDialog(false);
        setContactToDeleteId(null);
    };


    const handleDeleteConfirmed = async () => {
        handleCloseDialog();

        if (!contactToDeleteId) return;

        try {
            const response = await fetch(`${BASE_URL}/contacts/${contactToDeleteId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {

                throw new Error('Failed to delete contact');
            }

            setContacts(contacts.filter((contact) => contact._id !== contactToDeleteId));

            setSnackbar({ open: true, message: 'Contact deleted successfully', severity: 'success' });
        } catch (err) {


            setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
        }
    };


    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" flexDirection="column">
                <CircularProgress sx={{ color: '#24803F' }} />
                <Typography variant="h6" mt={2}>Loading contacts...</Typography>
            </Box>
        );
    }


    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <Alert severity="error">Error: {error}</Alert>
            </Box>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Box sx={{ mt: 2, borderRadius: 2, p: 3, bgcolor: '#fff', boxShadow: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                        Contact
                    </Typography>
                    <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
                        Dashboard • Contact • List
                    </Typography>
                </motion.div>

                {contacts.length === 0 ? (
                    <Typography variant="h6" align="center" sx={{ mt: 4, color: 'text.secondary' }}>
                        No contacts available
                    </Typography>
                ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Table aria-label="contact messages table">

                            <GradientTableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Message</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </GradientTableHead>
                            <AnimatePresence>
                                <TableBody>
                                    {contacts.map((contact) => (
                                        <motion.tr
                                            key={contact._id}
                                            variants={itemVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            layout
                                            style={{
                                                display: 'table-row'
                                            }}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                }
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                onClick={() => handleOpenDetails(contact)}
                                                sx={{
                                                    color: "#24803F",
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    }
                                                }}
                                            >
                                                {contact.name.toUpperCase()}
                                            </TableCell>
                                            <TableCell>{contact.email}</TableCell>
                                            <TableCell>{contact.phone}</TableCell>
                                            <TableCell>
                                                {truncateText(contact.message)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleOpenDialog(contact._id)}
                                                    sx={{ borderRadius: 1.5 }}
                                                    component={motion.button}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </motion.tr>
                                    ))}
                                </TableBody>
                            </AnimatePresence>
                        </Table>
                    </TableContainer>
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', borderRadius: 1.5, boxShadow: 6 }}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <DialogTitle id="alert-dialog-title" sx={{ color: '#24803F', fontWeight: 'bold' }}>
                        {"Confirm Deletion"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to delete this contact message? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} sx={{ color: 'text.secondary' }}>Cancel</Button>
                        <Button onClick={handleDeleteConfirmed} autoFocus variant="contained" color="error">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>

            <Dialog
                open={openDetailsModal}
                onClose={handleCloseDetails}
                aria-labelledby="contact-details-title"
                PaperProps={{ sx: { borderRadius: 2, minWidth: 400 } }}
            >
                <DialogTitle id="contact-details-title" sx={{ fontWeight: 'bold', color: '#24803F' }}>
                    Contact Details
                </DialogTitle>
                <DialogContent dividers>
                    {selectedContact && (
                        <Box>
                            <Typography variant="subtitle2" color="#2e7d32" sx={{
                                fontWeight: "800",
                            }}>Name</Typography>
                            <Typography variant="body1" gutterBottom>{selectedContact.name}</Typography>

                            <Typography variant="subtitle2" color="#2e7d32" sx={{
                                fontWeight: "800",
                            }}>Email</Typography>
                            <Typography variant="body1" gutterBottom>{selectedContact.email}</Typography>

                            <Typography variant="subtitle2" color="#2e7d32" sx={{
                                fontWeight: "800",
                            }}>Phone</Typography>
                            <Typography variant="body1" gutterBottom>{selectedContact.phone}</Typography>

                            <Typography variant="subtitle2" color="#2e7d32" sx={{
                                fontWeight: "800",
                            }}>Message</Typography>
                            <Typography variant="body1" gutterBottom whiteSpace="pre-line">{selectedContact.message}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails} sx={{
                        color: "#2e7d32",
                    }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </motion.div >
    );
};

export default Contact;
